import React, { useState, useRef, useEffect, type ChangeEvent } from 'react';
import {
  List,
  ListItem,
  TextField,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  useTheme,
  Card,
  Menu,
  MenuItem,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import io, { type Socket } from 'socket.io-client';
import { useParams } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { colorTheme } from 'theme';
import { type RootState } from 'store';
import { useSelector } from 'react-redux';
import {
  createComplaintMessages,
  getAllChatByComplaintId,
  uploadChatImage,
} from 'service/ChatService';
import {
  type ChatMessagesAttributesType,
  type ComplaintAttributesType,
} from 'types/services';
import { getComplaintByComplaintId } from 'service/ComplaintService';
import CloseIcon from '@mui/icons-material/Close';
import { GridSearchIcon } from '@mui/x-data-grid';
import { strings } from 'i18n/Strings';
import {
  customerChatsConstants,
  roles as allRoles,
} from 'constants/constantText';
import { ImageViewModal } from 'components/shared';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ComplaintDetails from './ComplaintDetails';
import Logo from '../../assets/images/Logo100X132.png';
import { formatChatDate } from 'components/helpers/commonFunctions/ChatDateTimeCalculation';
import { getAccessToken } from 'service/AuthService';
import { getResource } from 'service/UserService';
import ComplaintChatMessageItem from './ComplaintsChatRecord';

interface SocketData {
  senderId: number;
  text: {
    text: string;
    image?: string;
  };
}
const CustomerComplaints = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [message, setMessage] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState<boolean>(false);
  const [openMenuDialog, setOpenMenuDialog] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessagesAttributesType[]>([]);
  const [selectedTab, setSelectedTab] = useState<string | null>('');
  const [complaint, setComplaint] = useState<ComplaintAttributesType>();
  const [profileImage, setProfileImage] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const { complaintId } = useParams();
  // const options = { timeZone: 'UTC' };
  const { roles } = useSelector((state: RootState) => state.auth);
  const { empId } = useSelector((state: RootState) => state.onboarding);
  const receiverId = parseInt(complaintId as string, 10);
  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
  const UserId = receiverId + 'M';
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    void handlegetAllMessages();
    void handlegetComplaintDetails();
  }, [complaintId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setSelectedTab(searchParams.get('tab'));
    // if url has tab then set value to complaints tab
    if (selectedTab === 'complaints') {
      sessionStorage.setItem('customerDetailsTab', 'complaints');
    }
  }, [selectedTab]);

  const handlegetAllMessages = async (): Promise<void> => {
    try {
      const res: { basicInfo: ChatMessagesAttributesType[] } =
        await getAllChatByComplaintId(receiverId);
      if (res?.basicInfo) {
        setMessages(res?.basicInfo);
      }
    } catch (err) {}
  };
  const handlegetComplaintDetails = async (): Promise<void> => {
    try {
      const res: { basicInfo: ComplaintAttributesType[] } =
        await getComplaintByComplaintId(receiverId);
      if (res?.basicInfo) {
        setComplaint(res?.basicInfo);
        // setProfileImage(res?.basicInfo?.user?.profileImage);

        const profilePic = await getResource(res.basicInfo.user?.profileImage);
        if (profilePic?.url) {
          setProfileImage(profilePic.url);
        }

        setUserId(res?.basicInfo?.userId);
      }
    } catch (err) {}
  };
  useEffect(() => {
    const accessToken = getAccessToken();
    socketRef.current = io(process.env.REACT_APP_MARSHMALLOW_API, {
      auth: {
        token: accessToken,
      },
    });
    // socketRef.current = io('http://localhost:3000');
    socketRef.current.on('getComplaintMessage', (data: SocketData) => {
      setMessages((messages) => [
        ...messages,
        {
          meassageid: data?.senderId,
          message: data?.text?.text,
          senderType: 'customer',
          imageUri: data?.text?.image,
          fileType: data?.text?.fileType,
          createdAt: new Date().toISOString(),
        },
      ]);
    });
    socketRef.current.emit('addUser', UserId);
    // socketRef.current.on('getUsers', (users) => {
    // });
    // Clean up the socket when the component unmounts
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  const sendMessage = (): void => {
    if (selectedImage) {
      handleSelectImage();
    } else {
      const newMessage = {
        message,
        senderType: 'employee',
        createdAt: new Date().toISOString(),
      };
      const createMessage = {
        complaintId,
        senderType: 'employee',
        senderId: empId,
        messagerType: 'complaint',
        message,
        userId,
      };
      setMessages((messages) => [...messages, newMessage]);
      socketRef.current?.emit('sendComplaintMessage', {
        senderId: parseInt(UserId),
        receiverId,
        text: newMessage,
        senderType: 'employee',
      });
      void createComplaintMessages(createMessage);
      setMessage('');
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      sendMessage();
    }
  };
  const handleFileInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      setSelectedImage(file);
      reader.onload = (e) => {
        setMessage('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectImage = (): void => {
    void uploadChatImage(selectedImage, Number(complaint?.userId)).then(
      (res: { chatImage: string }) => {
        if (res?.chatImage) {
          const createImgMessage = {
            complaintId,
            senderType: customerChatsConstants.employee,
            senderId: empId,
            message,
            messagerType: customerChatsConstants.complaint,
            imageUri: res?.chatImage,
            fileType: selectedImage?.type,
          };
          void createComplaintMessages(createImgMessage)
            .then((data) => {
              if (data) {
                const newMessage = {
                  id: messages.length + 1,
                  message,
                  senderType: customerChatsConstants.employee,
                  imageUri: res?.chatImage,
                  fileType: selectedImage?.type,
                  createdAt: new Date().toISOString(),
                };

                socketRef.current?.emit('sendComplaintMessage', {
                  senderId: parseInt(UserId),
                  receiverId,
                  text: newMessage,
                  senderType: customerChatsConstants.employee,
                });
                setMessages((messages) => [...messages, newMessage]);
              }
            })
            .catch((err) => {
              console.log(err);
            });
        }
      },
    );
    setMessage('');
    setSelectedImage(null);
  };

  const handleImageDialogClose = (): void => {
    setOpenImageDialog(false);
  };

  const handleMenuDialogClose = (): void => {
    setOpenMenuDialog(false);
  };

  const filteredMessages = messages.filter((message) => {
    return message?.message.toLowerCase().includes(search.toLowerCase());
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const customStyles = {
    mainCardStyle: {
      flexGrow: 1,
      overflowY: 'auto',
      display: 'flex',
      height: '85vh',
      flexDirection: 'column',
      borderRadius: '5px',
    },
    mainBoxStyle: {
      display: 'flex',
      padding: '10px 10px',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid lightgray',
    },
    subBoxStyle: {
      display: 'flex',
      alignItems: 'center',
    },
    avatarBoxStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: '2px solid lightgray',
    },
    avatarNameStyle: {
      width: '24px',
      height: '24px',
      fontSize: '13px',
    },
    nameStyle: {
      fontSize: '13px',
      fontWeight: 600,
      marginLeft: '-15px',
    },
    inputFieldStyle: {
      marginRight: '20px',
      width: '416px',
      '& input': {
        borderColor: colors.solitude, // Change this to your desired color
      },
    },
    boxPaddingStyle: {
      padding: '5px',
    },
    msgContainerStyle: {
      flexGrow: 1,
      overflow: 'auto',
    },
    commonFlexStyle: {
      display: 'flex',
    },
    msgAvatarBoxStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: '2px solid lightgray',
    },
    msgAvatarStyle: {
      width: '24px',
      height: '24px',
      fontSize: '13px',
    },
    msgBoxStyle: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '-15px',
      marginRight: '-25px',
    },
    subMsgBoxStyle: {
      padding: '10px',
      maxWidth: '460px',
      minWidth: '100px',
      minHeight: '36px',
      borderRadius: '5px',
      wordWrap: 'break-word',
    },
    msgTypoStyle: {
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '24px',
    },
    cursorStyle: { cursor: 'pointer' },
    maxWidthStyle: {
      maxWidth: '100%',
    },
    showBoxStyle: {
      display: 'flex',
      justifyContent: 'flex-end',
      cursor: 'pointer',
    },
    typoColorStyle: {
      color: colors.primaryRed[100],
    },
    chipBoxStyle: {
      display: 'flex',
      justifyContent: 'center',
      cursor: 'pointer',
      marginTop: '5px',
    },
    timeMainBoxStyle: {
      display: 'flex',
      flexDirection: 'column',
    },
    timestampStyle: {
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: '20px',
    },
    textFieldBoxStyle: {
      display: 'flex',
      alignItems: 'center',
      padding: 1,
      justifyContent: 'center',
      textAlign: 'center',
    },
    imageStyle: {
      maxWidth: '50px',
      maxHeight: '50px',
    },
    closeIconStyle: {
      maxWidth: '15px',
      maxHeight: '15px',
      marginBottom: '20px',
      backgroundColor: colors.secondaryGreen[200],
      borderRadius: '10px',
    },
    fileIconStyle: {
      position: 'absolute',
      right: 0,
      top: '50%',
      marginRight: 10,
    },
    marginRightStyle: {
      marginRight: '10px',
    },
    sendButtonStyle: {
      bgcolor: colors.greenAccent[500],
      borderRadius: 2,
      py: 1.5,
    },
    displayNone: {
      display: 'none',
    },
    emptyPensionContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '70vh',
      width: '100%',
    },
    emptyPensionText: {
      fontFamily: 'Inter!important',
      color: '#838383',
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.2,
    },
    profileImageStyle: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
    pdfIconStyle: {
      width: 100,
      height: 100,
    },
    logoImageStyle: {
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const,
      padding: '2px',
    },
  };

  return (
    <Card sx={customStyles.mainCardStyle}>
      <Box sx={customStyles.mainBoxStyle}>
        <Box sx={customStyles.subBoxStyle}>
          <ListItemAvatar>
            <Box sx={customStyles.avatarBoxStyle}>
              <Avatar sx={customStyles.avatarNameStyle}>
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt={'Profile Picture'}
                    style={customStyles.profileImageStyle}
                  />
                ) : (
                  <>
                    {complaint?.firstName
                      ? complaint?.firstName.slice(0, 1)
                      : complaint?.user?.firstName?.slice(0, 1)}
                    {complaint?.lastName
                      ? complaint?.lastName.slice(0, 1)
                      : complaint?.user?.lastName?.slice(0, 1)}
                  </>
                )}
              </Avatar>
            </Box>
          </ListItemAvatar>
          <Typography sx={customStyles.nameStyle}>
            {complaint?.firstName
              ? complaint?.firstName
              : complaint?.user?.firstName}{' '}
            {complaint?.lastName
              ? complaint?.lastName
              : complaint?.user?.lastName}
          </Typography>
        </Box>
        <Box>
          <TextField
            label={strings.displayText.search}
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            onChange={(event: {
              target: { value: React.SetStateAction<string> };
            }) => {
              setSearch(event.target.value);
            }}
            sx={customStyles.inputFieldStyle}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton>
                    <GridSearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setSearch('');
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <IconButton onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem
              onClick={(e) => {
                handleClose(e);
                setOpenMenuDialog(true);
              }}
            >
              Complaint Details
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box sx={customStyles.boxPaddingStyle}></Box>
      <Box sx={customStyles.msgContainerStyle}>
        <List>
          {filteredMessages.map((message) => (
            <ListItem
              key={message.messageId}
              sx={[
                customStyles.commonFlexStyle,
                {
                  justifyContent:
                    message.senderType === customerChatsConstants.employee
                      ? 'flex-end'
                      : 'flex-start',
                },
              ]}
            >
              <Box
                sx={[
                  customStyles.commonFlexStyle,
                  {
                    flexDirection:
                      message.senderType === customerChatsConstants.employee
                        ? 'row-reverse'
                        : 'row',
                  },
                ]}
              >
                <ListItemAvatar>
                  <Box
                    sx={[
                      customStyles.msgAvatarBoxStyle,
                      {
                        marginLeft:
                          message.senderType ===
                            customerChatsConstants.employee && 4,
                      },
                    ]}
                  >
                    <Avatar
                      sx={[
                        customStyles.msgAvatarStyle,
                        {
                          bgcolor:
                            message.senderType ===
                              customerChatsConstants.employee &&
                            colors.greenAccent[500],
                        },
                      ]}
                    >
                      {message.senderType ===
                      customerChatsConstants.employee ? (
                        <img
                          src={Logo}
                          alt="Zen Pensions logo"
                          style={customStyles.logoImageStyle}
                        />
                      ) : (
                        <>
                          {profileImage ? (
                            <>
                              <img
                                src={profileImage}
                                alt={'Profile Picture'}
                                style={customStyles.profileImageStyle}
                              />
                            </>
                          ) : (
                            <>
                              {complaint?.firstName
                                ? complaint?.firstName.slice(0, 1)
                                : complaint?.user?.firstName?.slice(0, 1)}
                              {complaint?.lastName
                                ? complaint?.lastName.slice(0, 1)
                                : complaint?.user?.lastName?.slice(0, 1)}
                            </>
                          )}
                        </>
                      )}
                    </Avatar>
                  </Box>
                </ListItemAvatar>

                <Box sx={customStyles.msgBoxStyle}>
                  <Box
                    sx={[
                      customStyles.subMsgBoxStyle,
                      {
                        backgroundColor:
                          message.senderType === customerChatsConstants.employee
                            ? colors.teaGreen[500]
                            : colors.turquoiseBlue[500],
                      },
                    ]}
                  >
                    <Typography sx={customStyles.msgTypoStyle}>
                      {message.message}
                    </Typography>
                    <ComplaintChatMessageItem
                      message={message}
                      customStyles={customStyles}
                      setOpenImageDialog={setOpenImageDialog}
                      setViewImage={setViewImage}
                    />

                    {/* <Box
                      onClick={async () => {
                        const res: { url: string } = await getResource(
                          message.imageUri,
                        );
                        if (!res?.url) return;
                        if (message?.fileType === 'application/pdf') {
                          const link = document.createElement('a');
                          link.href = res.url;
                          link.download = `download.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        } else {
                          setOpenImageDialog(true);
                          setViewImage(res.url);
                        }
                      }}
                      sx={customStyles.cursorStyle}
                    >
                      {message.imageUri && (
                        <>
                          {message?.fileType === 'application/pdf' ? (
                            <img
                              style={customStyles.pdfIconStyle}
                              src={PdfIcon}
                            />
                          ) : (
                            <img
                              src={`${String(process.env.REACT_APP_Image_Uri)}${
                                message.imageUri
                              }`}
                              style={customStyles.maxWidthStyle}
                            />
                          )}
                        </>
                      )}
                    </Box> */}
                  </Box>
                  <Box sx={customStyles.timeMainBoxStyle}>
                    <Box
                      sx={[
                        customStyles.commonFlexStyle,
                        {
                          justifyContent:
                            message.senderType ===
                            customerChatsConstants.employee
                              ? 'flex-end'
                              : 'flex-start',
                        },
                      ]}
                    >
                      <Typography
                        color={colors.secondaryGreen[200]}
                        sx={customStyles.timestampStyle}
                      >
                        {message?.createdAt &&
                          formatChatDate(message?.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </ListItem>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Box>
      <Box sx={customStyles.textFieldBoxStyle}>
        {roles[0] === allRoles.MMEmployeeRole && (
          <TextField
            label={strings.displayText.typeAMessage}
            variant="outlined"
            fullWidth
            value={message}
            onChange={(event: {
              target: { value: React.SetStateAction<string> };
            }) => {
              setMessage(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            style={{ position: 'relative' }}
            InputProps={{
              startAdornment: selectedImage && (
                <InputAdornment position="start">
                  <img
                    src={URL.createObjectURL(selectedImage)}
                    alt="Selected"
                    style={customStyles.imageStyle}
                  />
                  <IconButton
                    onClick={() => {
                      setSelectedImage(null);
                    }}
                  >
                    <CloseIcon sx={customStyles.closeIconStyle} />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  style={customStyles.fileIconStyle}
                >
                  <IconButton
                    sx={customStyles.marginRightStyle}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <AttachFileIcon />
                  </IconButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={false}
                    loadingPosition="end"
                    onClick={sendMessage}
                    sx={customStyles.sendButtonStyle}
                  >
                    <Typography component="h1" variant="h5">
                      {strings.displayText.send}
                    </Typography>
                  </LoadingButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        <input
          type="file"
          ref={fileInputRef}
          style={customStyles.displayNone}
          onChange={handleFileInputChange}
          accept="image/*,application/pdf"
        />
        <ImageViewModal
          open={openImageDialog}
          onClose={handleImageDialogClose}
          viewImageUrl={viewImage}
          title={`${String(strings.displayText.imageView)}`}
        />

        <ComplaintDetails
          open={openMenuDialog}
          onClose={handleMenuDialogClose}
          complaint={complaint}
          title={`${String(strings.displayText.complaintDetails)}`}
        />
      </Box>
    </Card>
  );
};
export default CustomerComplaints;
